import React, { ReactNode, useImperativeHandle, useState } from "react";
import { Cropper } from "react-cropper";
import { Accept, useDropzone } from "react-dropzone";
import FileUploadIcon from "../icons/FileUploadIcon";
import { Box } from "../ui/chakra";
import InputError from "../ui/InputError";
import type { InputErrorVariant } from "../ui/InputError";


import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";

const Dropzone = ({
  onDrop: propsOnDrop,
  accept = {},
  name = "",
  multiple = false,
  message = undefined,
  error = undefined,
  croppable = false,
  aspectRatio = 1 / 1,
  inputRef = undefined,
  errorVariant = "text",
}: {
  onDrop: (acceptedFiles: File[]) => void;
  accept?: Accept;
  name?: string;
  multiple?: boolean;
  message?: ReactNode;
  error?: string;
  croppable?: boolean;
  aspectRatio?: number;
  inputRef?: React.Ref<HTMLInputElement>;
  errorVariant?: InputErrorVariant;
}) => {
  const onDrop = (acceptedFiles: File[]) => {
    if (croppable) {
      setInputImage(acceptedFiles[0]);
      setOpen(true);
    } else {
      propsOnDrop(acceptedFiles);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    inputRef: dropzoneInputRef,
  } = useDropzone({
    onDrop: onDrop,
    accept: accept,
    multiple,
  });

  useImperativeHandle(inputRef, () => {
    return {
      click() {
        dropzoneInputRef.current?.click();
      },
      focus() {
        dropzoneInputRef.current?.focus();
      },
      scrollIntoView() {
        dropzoneInputRef.current?.focus();
      },
    } as HTMLInputElement;
  }, [dropzoneInputRef]);

  const [open, setOpen] = useState<boolean>(false);
  const [cropper, setCropper] = useState<Cropper>();
  const [inputImage, setInputImage] = useState<File>();

  const getCropData = () => {
    if (cropper == null) {
      return;
    }

    const croppedCanvas = cropper.getCroppedCanvas();

    croppedCanvas.toBlob((blob) => {
      const file = new File([blob!], inputImage!.name, {
        type: inputImage!.type,
      });
      propsOnDrop([file]);
    }, inputImage!.type);
    setOpen(false);
  };

  return (
    <Box>
      <Stack
        {...getRootProps()}
        height={40}
        border="2px dashed #7D8B88"
        align="center"
        justify="center"
        borderRadius="2px"
        cursor="pointer"
        gap={0}
        bgColor="brand.50"
        px={4}
      >
        <input
          {...getInputProps()}
          name={name}
          data-testid={`file-input-${name}`}
        />
        {isDragActive ? (
          <Text color="#82908D" fontWeight="600" mt={2}>
            ファイルをここにドロップ...
          </Text>
        ) : (
          <>
            <FileUploadIcon color="#82908D" boxSize={10} />
            <Text
              color="#82908D"
              fontWeight="600"
              mt={2}
              fontSize={{ base: "sm", md: "md" }}
            >
              ファイルをここにドラッグ＆ドロップ
            </Text>
            <Text color="#82908D" fontSize={{ base: "xs", md: "sm" }}>
              もしくはクリックしてファイルを選択
            </Text>
          </>
        )}
      </Stack>
      {message != null && (
        <Text fontSize="xs" color="#6D787D" mt={"6px"}>
          {message}
        </Text>
      )}
      {error != null && (
        <Box mt={message != null ? "6px" : 2}>
          <InputError variant={errorVariant}>{error}</InputError>
        </Box>
      )}
      <Modal isOpen={open} onClose={() => setOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`範囲を設定する`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <div className="image-cropper-modal">
              <Cropper
                id="image-cropper"
                style={{ height: 400, width: "100%" }}
                src={inputImage ? URL.createObjectURL(inputImage) : ""}
                initialAspectRatio={aspectRatio}
                aspectRatio={aspectRatio}
                onInitialized={(instance) => {
                  setCropper(instance);
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button type="button" onClick={getCropData}>
              選択
            </button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dropzone;
